import React, { createContext, ReactNode, useContext } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useGetWarehouseSkusImportErrorCountQuery } from '../../hooks';

import { isWarehouseTab, TABS } from './interfaces';

interface WarehouseType {
  activeTab: TABS;
  isEditing: boolean;
  setActiveTab: (tab: TABS) => void;
  setEditing: (value: boolean) => void;
  editDisabled: boolean;
  setEditDisabled: (value: boolean) => void;
}

const WarehouseContextType = createContext<WarehouseType>(null!);

function WarehouseProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const params = useParams<{ id: string; tab?: string }>();
  const [isEditing, setEditing] = React.useState(false);
  const [editDisabled, setEditDisabled] = React.useState(false);
  const activeTab = isWarehouseTab(params.tab) ? params.tab : TABS.SETTINGS;
  const { data: importErrorsCount } = useGetWarehouseSkusImportErrorCountQuery(params?.id || '');
  const hasImportErrors = (importErrorsCount?.count ?? 0) > 0;

  const setActiveTab = (tab: TABS) => {
    if (!params.id || tab === activeTab || (tab === TABS.IMPORT_ERRORS && !hasImportErrors)) {
      return;
    }

    navigate(`/dashboard/warehouses/${params.id}/${tab}`);
  };

  const value = {
    activeTab,
    isEditing,
    setActiveTab,
    setEditing,
    editDisabled,
    setEditDisabled,
  };

  React.useEffect(() => {
    setEditing(false);
  }, [activeTab]);

  React.useEffect(() => {
    if (params.id && !isWarehouseTab(params.tab)) {
      navigate(`/dashboard/warehouses/${params.id}/${TABS.SETTINGS}`, { replace: true });
    }
  }, [navigate, params.id, params.tab]);

  React.useEffect(() => {
    if (params.id && activeTab === TABS.IMPORT_ERRORS && importErrorsCount && !hasImportErrors) {
      navigate(`/dashboard/warehouses/${params.id}/${TABS.SETTINGS}`, { replace: true });
    }
  }, [activeTab, hasImportErrors, importErrorsCount, navigate, params.id]);

  return <WarehouseContextType.Provider value={value}>{children}</WarehouseContextType.Provider>;
}

const useWarehouse = () => {
  const context = useContext(WarehouseContextType);

  if (context === undefined) {
    throw new Error('useRolesState must be used within a RolesProvider');
  }

  return context;
};

export { WarehouseContextType, useWarehouse, WarehouseProvider };
